import { useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../stores/gameStore';
import { drawFrame } from '../utils/canvasHelpers';

export const useGameLoop = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
  const loopRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);
  const shakeRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const {
    phase,
    tick,
    snake,
    food,
    score,
    currentLevel,
    gridWidth,
    gridHeight,
    colorblindMode
  } = useGameStore();

  const animate = useCallback(
    (time: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dt = Math.min((time - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = time;

      // Update game tick (Snake movement)
      if (phase === 'playing') {
        tick();
        
        // Decay shake
        shakeRef.current.x *= 0.9;
        shakeRef.current.y *= 0.9;
      }

      // Render frame
      drawFrame(ctx, {
        snake,
        food,
        score,
        currentLevel,
        phase,
        colorblindMode,
        gridWidth,
        gridHeight,
        shake: shakeRef.current
      });

      loopRef.current = requestAnimationFrame(animate);
    },
    [
      canvasRef,
      phase,
      tick,
      snake,
      food,
      score,
      currentLevel,
      gridWidth,
      gridHeight,
      colorblindMode
    ]
  );

  useEffect(() => {
    lastTimeRef.current = performance.now();
    loopRef.current = requestAnimationFrame(animate);
    return () => {
      if (loopRef.current) cancelAnimationFrame(loopRef.current);
    };
  }, [animate]);

  // Add screen shake on game over
  useEffect(() => {
    if (phase === 'gameOver') {
      shakeRef.current.x = (Math.random() - 0.5) * 20;
      shakeRef.current.y = (Math.random() - 0.5) * 20;
    }
  }, [phase]);

  return { shakeRef };
};
